import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useState } from "react";

import { Page } from "@/components/Page";
import {
  AddUserContext,
  RemoveUserContext,
  SubscriberListTable,
} from "@/components/SubscriberListTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { H1 } from "@/components/ui/typography";
import { createSubscriberList, SubscriptionUser } from "@/lib/api";
import { User } from "@/lib/types";

export const Route = createFileRoute(
  "/app/_sidebar-layout/subscriber-lists/create",
)({
  component: CreateSubscriberListPage,
  head: () => ({ meta: [{ title: "Create Subscriber List" }] }),
});

function CreateSubscriberListPage() {
  const [users, setUsers] = useState<SubscriptionUser[]>([]);
  const onRemoveUser = useCallback(
    (username: string) => {
      setUsers((prev) => prev.filter((u) => u.username !== username));
    },
    [setUsers],
  );
  const onAddUser = useCallback(
    (user: User) => {
      setUsers((prev) => [
        ...prev,
        {
          avatar: user.avatar,
          username: user.handle,
          display_name: user.display_name,
          name: user.display_name,
          user_id: user.id,
        },
      ]);
    },
    [setUsers],
  );
  const navigate = useNavigate({ from: "/app/subscriber-lists/create" });
  const onCreate = useMutation({
    mutationFn: async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const form = new FormData(e.target as HTMLFormElement);
      const display_name = form.get("display-name") as string;
      const list_type = form.get("list_type") as "memberships" | "blocklists";

      await createSubscriberList({
        display_name,
        usernames: users.map((u) => u.username),
        list_type,
      });
      navigate({ to: "/app/subscriber-lists" }).catch(console.error);
    },
  });

  return (
    <RemoveUserContext value={onRemoveUser}>
      <AddUserContext value={onAddUser}>
        <Page className="md:gap-10">
          <H1>Create Subscriber List</H1>
          <form
            className="flex flex-col gap-8"
            onSubmit={(e) => onCreate.mutate(e)}
          >
            <div className="flex flex-col gap-2">
              <label htmlFor="list-type" className="font-medium">
                List Type
              </label>
              <Select name="list_type" defaultValue="memberships">
                <SelectTrigger id="list-type">
                  <SelectValue placeholder="Select one of your feeds" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="memberships">Membership</SelectItem>
                  <SelectItem value="blocklists">Blocklist</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <label htmlFor="display-name" className="font-medium">
                Display Name
              </label>
              <Input
                required
                type="text"
                id="display-name"
                name="display-name"
                className="w-full rounded-md border border-slate-6 dark:border-slatedark-6 bg-slate-2 dark:bg-slatedark-2 px-3 py-2 text-sm text-slate-normal placeholder:text-slate-dim focus-visible:outline-hidden focus-visible:ring-2  focus-visible:ring-offset-2"
              />
            </div>

            <div className="font-medium">Included Users</div>
            <SubscriberListTable users={users} />
            <div className="flex">
              <div className="flex-1" />
              <Button isLoading={onCreate.isPending} type="submit">
                Create
              </Button>
            </div>
          </form>
        </Page>
      </AddUserContext>
    </RemoveUserContext>
  );
}
